import { ApprovalStepsEditor, type ApprovalStep } from '@/components/features/ServiceRequests/ApprovalStepsEditor';
import { type TemplateField } from '@/components/features/ServiceRequests/DynamicRequestForm';
import { TemplateFieldEditor } from '@/components/features/ServiceRequests/TemplateFieldEditor';
import { PageContent } from '@/components/layout/PageContent';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/shadcn/button';
import { Checkbox } from '@/components/ui/shadcn/checkbox';
import { Input } from '@/components/ui/shadcn/input';
import { Label } from '@/components/ui/shadcn/label';
import { Textarea } from '@/components/ui/shadcn/textarea';
import { useTranslations } from '@/hooks/useTranslations';
import AppLayout from '@/layouts/app-layout';
import { type PageProps } from '@/types';
import { Head, Link, router, useForm, usePage } from '@inertiajs/react';
import { route } from 'ziggy-js';

interface EditTemplatePageProps extends PageProps {
    [key: string]: unknown;
    template: {
        id: string;
        slug: string;
        name: string;
        description: string | null;
        icon: string | null;
        category: string | null;
        is_active: boolean;
        sort_order: number;
        fields: TemplateField[];
        requires_approval: boolean;
        approval_steps: ApprovalStep[];
        final_notify_role: string | null;
        final_notify_user_id: string | null;
        subject_field_key: string | null;
    };
    roles: string[];
    users: Array<{ id: string; name: string }>;
}

export default function AdminServiceRequestTemplateEditPage() {
    const { props } = usePage<EditTemplatePageProps>();
    const { template, roles, users } = props;
    const { t } = useTranslations();

    const { data, setData, patch, processing, errors } = useForm({
        slug: template.slug,
        name: template.name,
        description: template.description ?? '',
        icon: template.icon ?? '',
        category: template.category ?? '',
        is_active: template.is_active,
        sort_order: template.sort_order,
        fields: template.fields,
        requires_approval: template.requires_approval ?? false,
        approval_steps: template.approval_steps ?? [],
        final_notify_role: template.final_notify_role ?? '',
        final_notify_user_id: template.final_notify_user_id ?? '',
        subject_field_key: template.subject_field_key ?? '',
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        patch(route('admin.service-request-templates.update', template.id));
    };

    const handleArchive = () => {
        if (confirm(t('service_requests.archive_confirm', 'Are you sure you want to archive this template?'))) {
            router.delete(route('admin.service-request-templates.destroy', template.id));
        }
    };

    const breadcrumbs = [
        { title: t('service_requests.admin_queue_title', 'Service Request Queue'), href: '/admin/service-requests' },
        { title: t('service_requests.template_manager_title', 'Request Templates'), href: '/admin/service-request-templates' },
        { title: template.name, href: route('admin.service-request-templates.show', template.id) },
        { title: 'Edit', href: '#' },
    ];

    return (
        <AppLayout breadcrumbs={breadcrumbs}>
            <Head title={`Edit: ${template.name}`} />

            <PageHeader
                title={`${t('service_requests.edit_template', 'Edit Template')}: ${template.name}`}
                actions={
                    <Button type="button" variant="destructive" size="sm" onClick={handleArchive}>
                        {t('service_requests.archive_template', 'Archive')}
                    </Button>
                }
            />

            <PageContent>
                <form onSubmit={handleSubmit} className="w-full space-y-6">
                    <div className="bg-card space-y-4 rounded-xl border p-6">
                        <h2 className="text-sm font-semibold tracking-wide uppercase">Template Details</h2>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div className="space-y-1.5">
                                <Label htmlFor="name">{t('service_requests.template_name', 'Template name')}</Label>
                                <Input id="name" value={data.name} onChange={(e) => setData('name', e.target.value)} />
                                {errors.name && <p className="text-destructive text-xs">{errors.name}</p>}
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="slug">{t('service_requests.template_slug', 'Slug')}</Label>
                                <Input id="slug" value={data.slug} onChange={(e) => setData('slug', e.target.value)} />
                                {errors.slug && <p className="text-destructive text-xs">{errors.slug}</p>}
                            </div>
                        </div>

                        <div className="space-y-1.5">
                            <Label htmlFor="description">{t('service_requests.template_description', 'Description')}</Label>
                            <Textarea id="description" value={data.description} onChange={(e) => setData('description', e.target.value)} rows={3} />
                        </div>

                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                            <div className="space-y-1.5">
                                <Label htmlFor="icon">{t('service_requests.template_icon', 'Icon')}</Label>
                                <Input id="icon" value={data.icon} onChange={(e) => setData('icon', e.target.value)} />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="category">{t('service_requests.template_category', 'Category')}</Label>
                                <Input id="category" value={data.category} onChange={(e) => setData('category', e.target.value)} />
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="sort_order">{t('service_requests.template_sort_order', 'Sort order')}</Label>
                                <Input
                                    id="sort_order"
                                    type="number"
                                    min={0}
                                    value={data.sort_order}
                                    onChange={(e) => setData('sort_order', Number(e.target.value))}
                                />
                            </div>
                        </div>

                        <div className="flex items-center gap-2">
                            <Checkbox id="is_active" checked={data.is_active} onCheckedChange={(v) => setData('is_active', Boolean(v))} />
                            <Label htmlFor="is_active" className="cursor-pointer">
                                {t('service_requests.template_active', 'Active')}
                            </Label>
                        </div>
                    </div>

                    <div className="bg-card space-y-4 rounded-xl border p-6">
                        <h2 className="text-sm font-semibold tracking-wide uppercase">{t('service_requests.template_fields', 'Fields')}</h2>
                        <TemplateFieldEditor fields={data.fields} onChange={(fields) => setData('fields', fields)} />
                    </div>

                    <div className="bg-card space-y-4 rounded-xl border p-6">
                        <h2 className="text-sm font-semibold tracking-wide uppercase">{t('service_requests.signoff.section_title', 'Sign-off')}</h2>
                        <ApprovalStepsEditor
                            value={{
                                requires_approval: data.requires_approval,
                                approval_steps: data.approval_steps,
                                final_notify_role: data.final_notify_role,
                                final_notify_user_id: data.final_notify_user_id,
                                subject_field_key: data.subject_field_key,
                            }}
                            fields={data.fields}
                            roles={roles}
                            users={users}
                            onChange={(patch) => setData((prev) => ({ ...prev, ...patch }))}
                        />
                    </div>

                    <div className="flex justify-end gap-3">
                        <Button type="button" variant="outline" asChild>
                            <Link href={route('admin.service-request-templates.show', template.id)}>Cancel</Link>
                        </Button>
                        <Button type="submit" disabled={processing}>
                            {processing ? t('service_requests.saving', 'Saving...') : t('service_requests.save_changes', 'Save changes')}
                        </Button>
                    </div>
                </form>
            </PageContent>
        </AppLayout>
    );
}
